import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {ReactNode} from 'react';

import {assertAndContinue} from 'Asserts';
import {VisualizationType} from 'insights/analytics-visualizations/AnalyticsVisualizationTypes';
import {PANE_MARGIN} from 'insights/InsightsConstants';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';
import {TabContainer, Tab} from 'ui-library/TabContainer';

export enum ConfigEditorTab {
  SETUP_TAB = 'SETUP_TAB',
  ANALYSIS_TAB = 'ANALYSIS_TAB',
  FORMAT_TAB = 'FORMAT_TAB',
}

// We manage the tab state per visualization type so that the state isn't transferred between chart types when
// changing chart types. This can cause errors if a chart type doesn't support the previous's opened tab.
export const configEditorTabState = makeUseGlobalClientState<{[key in VisualizationType]?: ConfigEditorTab}>(
  {}
);

export interface TabbedConfigEditorSkeletonProps {
  setupTabContent: ReactNode;
  analysisTabContent?: ReactNode;
  formatTabContent?: ReactNode;
  enableAnalysisTab?: boolean;
  enableFormatTab?: boolean;
  visualizationConfigId: string;
  visualizationType: VisualizationType;
}

export default function TabbedConfigEditorSkeleton({
  setupTabContent,
  analysisTabContent,
  formatTabContent,
  enableAnalysisTab = true,
  enableFormatTab = true,
  visualizationConfigId,
  visualizationType,
}: TabbedConfigEditorSkeletonProps): JSX.Element {
  assertAndContinue(
    setupTabContent != null || analysisTabContent != null || formatTabContent != null,
    'Expected at least one of setupTabContent, analysisTabContent, or formatTabContent to be specified'
  );
  const [activeTabs, setActiveTabs] = configEditorTabState.useGlobalClientState(visualizationConfigId);
  const activeTab = activeTabs[visualizationType] ?? ConfigEditorTab.SETUP_TAB;

  function changeTab(newTab: ConfigEditorTab): void {
    setActiveTabs({
      ...activeTabs,
      [visualizationType]: newTab,
    });
  }

  return (
    <TabContainer<ConfigEditorTab>
      activeTab={activeTab}
      onRequestTabChange={changeTab}
      className={css(tabContainerStyles.wrapper)}
      navClassName={css(tabContainerStyles.header)}
      paneClassName={classNames('scrollbar-override', css(tabContainerStyles.pane))}
    >
      {setupTabContent && (
        <Tab tabKey={ConfigEditorTab.SETUP_TAB} title="Setup">
          {setupTabContent}
        </Tab>
      )}
      {enableAnalysisTab
        ? analysisTabContent && (
            <Tab tabKey={ConfigEditorTab.ANALYSIS_TAB} title="Analysis">
              {analysisTabContent}
            </Tab>
          )
        : undefined}
      {enableFormatTab
        ? formatTabContent && (
            <Tab tabKey={ConfigEditorTab.FORMAT_TAB} title="Format">
              {formatTabContent}
            </Tab>
          )
        : undefined}
    </TabContainer>
  );
}

const tabContainerStyles = StyleSheet.create({
  wrapper: {
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    padding: `0px ${PANE_MARGIN}px`,
  },
  pane: {
    marginTop: 0,
    padding: `0px ${PANE_MARGIN}px ${PANE_MARGIN}px ${PANE_MARGIN}px`,
    overflowY: 'auto',
  },
});
